// src/modules/app-views/_apps/appmulti/operators/pages/Document/tabs/ApiIntegrationTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Row,
  Col,
  Flex,
  Table,
  Tag,
  Tooltip,
  Descriptions,
  Badge,
  Upload,
  theme,
} from 'antd';
import {
  ReloadOutlined,
  InfoCircleOutlined,
  UserOutlined,
  RobotOutlined,
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimplePassword from 'components/SimpleComponents/SimplePassword';
import SimpleSwitch from 'components/SimpleComponents/SimpleSwitch';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import translateX from 'utils/translateX';
import { parseDateToDisplay } from 'utils/helpers';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import operatorsService from 'modules/app-views/_apps/appmulti/operators/service';
import attachmentsService from 'services/attachmentsService';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const STYLES = {
  container: { position: 'relative', minHeight: 120 },
  credentialsAlert: { marginTop: 12 },
  exchangeRateAlert: { marginTop: 12, fontSize: 11 },
  currencyInput: { width: 60 },
  countSpan: { fontSize: 12, fontWeight: 500, minWidth: 16 },
  descriptionSpan: { fontSize: 12 },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const OperatorsApiIntegrationTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data } = useDocumentContext();
  const { locked, form } = useSimpleFormContext();
  const { token } = theme.useToken();
  const pdfIconStyle = { fontSize: 24, color: token.colorError };

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Auto-detect provider from CNPJ
  // Backend returns CNPJ in data.documents array, not data.cnpj
  const cnpjValue =
    data?.documents?.find(d => d.documentType === 'cnpj')?.value || data?.cnpj?.value || '';
  const detectedProvider = operatorsService.resolveProviderByCnpj(cnpjValue);
  const hasApiMatch = !!detectedProvider;
  const apiEnabled = data?._integrations?.apiEnabled || false;
  const formIntegrations = form?.getFieldsValue(true)?._integrations;
  const hasCredentials = !!(
    (data?._integrations?.apiCredentials?.login || formIntegrations?.apiCredentials?.login) &&
    (data?._integrations?.apiCredentials?.password || formIntegrations?.apiCredentials?.password)
  );

  // Build SimpleCurrency-compatible currencies from operator service
  const currencies = buildCurrencyOptions();

  // Determine alert state
  let alertType = 'warning';
  let alertMessage = 'no_api_integration_warning';

  if (!hasApiMatch) {
    alertType = 'error';
    alertMessage = 'no_api_for_cnpj';
  } else if (!apiEnabled) {
    alertType = 'warning';
    alertMessage = 'api_available_but_disabled';
  } else if (!hasCredentials) {
    alertType = 'warning';
    alertMessage = 'api_credentials_required';
  } else {
    alertType = 'info';
    alertMessage = 'api_synced_daily';
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsData, setLogsData] = useState([]);
  const [validatingCredentials, setValidatingCredentials] = useState(false);
  const [currentApiEnabled, setCurrentApiEnabled] = useState(apiEnabled);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [exchangeRateKey, setExchangeRateKey] = useState(0); // Force re-render counter
  const [currentCurrency, setCurrentCurrency] = useState(
    data?.parameters?.__exchangeRateSuffix || 'USD'
  );
  const [lastSyncDate, setLastSyncDate] = useState(null); // Last exchange sync date
  const [conditionsFileUrl, setConditionsFileUrl] = useState(
    data?.parameters?.conditionsFileUrl || null
  );
  const [conditionsUploading, setConditionsUploading] = useState(false);

  // Disable sync button if conditions not met (use currentApiEnabled for real-time reactivity)
  // Note: credentials are NOT required here — central hub sync works without them
  const isSyncDisabled = !hasApiMatch || !currentApiEnabled || locked;

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (data?._id) {
      fetchApiLogs();
    }
  }, [data?._id]);

  // Sync currentApiEnabled with data
  useEffect(() => {
    setCurrentApiEnabled(data?._integrations?.apiEnabled || false);
  }, [data?._integrations?.apiEnabled]);

  // Sync currentCurrency with data when it loads
  useEffect(() => {
    if (data?.parameters?.__exchangeRateSuffix) {
      setCurrentCurrency(data.parameters.__exchangeRateSuffix);
    }
  }, [data?.parameters?.__exchangeRateSuffix]);

  useEffect(() => {
    const url = data?.parameters?.conditionsFileUrl || null;
    setConditionsFileUrl(url);
    // Sync form store so handleFinish can read via getFieldValue
    form?.setFieldValue(['parameters', 'conditionsFileUrl'], url);
  }, [data?.parameters?.conditionsFileUrl]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  async function fetchApiLogs() {
    setLogsLoading(true);
    try {
      const operatorId = data?._id;

      const [docs] = await axiosService.get({
        url: `/v1/api-logs/get-list?operatorId=${operatorId}`,
        returnAsObject: false,
      });

      // Backend should return docs directly, but handle both cases
      const logsArray = Array.isArray(docs) ? docs : [];

      setLogsData(logsArray);
    } catch (error) {
    } finally {
      setLogsLoading(false);
    }
  }

  async function handleApiEnabledChange(checked, form) {
    // If disabling, no validation needed
    if (!checked) {
      form.setFieldValue(['_integrations', 'apiEnabled'], false);
      setCurrentApiEnabled(false); // Update state immediately
      return;
    }

    // If enabling, validate credentials first
    const currentValues = form.getFieldsValue();
    const login = currentValues?._integrations?.apiCredentials?.login;
    const password = currentValues?._integrations?.apiCredentials?.password;

    // Check if credentials are filled
    if (!login || !password) {
      // Show error notification
      appService.notification({
        flag: 'error',
        title: translateX('api_credentials'),
        description: translateX('api_credentials_required_to_enable'),
      });

      // Keep switch OFF
      form.setFieldValue(['_integrations', 'apiEnabled'], false);

      // Mark empty fields as error and focus on first empty field
      const fieldsToMark = [];

      if (!login) {
        fieldsToMark.push({
          name: ['_integrations', 'apiCredentials', 'login'],
          errors: [translateX('required_field')],
        });
      }

      if (!password) {
        fieldsToMark.push({
          name: ['_integrations', 'apiCredentials', 'password'],
          errors: [translateX('required_field')],
        });
      }

      // Set field errors
      form.setFields(fieldsToMark);

      // Focus on first empty field using DOM query
      const firstEmptyFieldName = !login ? 'login' : 'password';

      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        // Try multiple selectors to find the input
        const selectors = [
          `input[id*="login"]`,
          `input[placeholder="xml12345"]`,
          `input[id*="apiCredentials_login"]`,
          `input[id*="_integrations_apiCredentials_login"]`,
        ];

        let inputElement = null;

        if (!login) {
          // Find login input
          for (const selector of selectors) {
            inputElement = document.querySelector(selector);
            if (inputElement) break;
          }
        } else {
          // Find password input
          inputElement = document.querySelector(`input[type="password"]`);
        }

        if (inputElement) {
          inputElement.focus();
          inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);

      return;
    }

    // Set validating state to lock Switch
    setValidatingCredentials(true);

    // Show loading message (appService.message is async, need await)
    const loadingController = await appService.message({
      flag: 'loading',
      text: translateX('validating_api_credentials'),
      delay: 0,
    });

    try {
      // Test credentials by calling getCambio endpoint
      // Use provider instead of operatorId (multi-tenant friendly)
      await axiosService.post({
        url: '/v1/api-integrations/test-credentials',
        data: {
          provider: detectedProvider.provider, // 'affinity' - auto-detected from CNPJ
          credentials: { login, password },
        },
        returnAsObject: true,
      });

      // Success - credentials are valid
      loadingController.close();
      setValidatingCredentials(false);
      form.setFieldValue(['_integrations', 'apiEnabled'], true);
      form.setFieldValue(['_integrations', 'providerCode'], detectedProvider.provider);
      // Explicitly preserve credentials — re-set before disabling fields to survive any
      // Ant Design form-store behavior that might clear values when disabled state changes
      form.setFieldValue(['_integrations', 'apiCredentials', 'login'], login);
      form.setFieldValue(['_integrations', 'apiCredentials', 'password'], password);
      setCurrentApiEnabled(true); // Update state immediately — fields become disabled after this

      appService.notification({
        flag: 'success',
        title: translateX('api_integration'),
        description: translateX('api_credentials_validated_successfully'),
      });
    } catch (error) {
      // Failure - credentials are invalid
      loadingController.close();
      setValidatingCredentials(false);
      form.setFieldValue(['_integrations', 'apiEnabled'], false);
      setCurrentApiEnabled(false); // Update state immediately

      // Mark credential fields as invalid
      form.setFields([
        {
          name: ['_integrations', 'apiCredentials', 'login'],
          errors: [translateX('invalid_api_credentials')],
        },
        {
          name: ['_integrations', 'apiCredentials', 'password'],
          errors: [translateX('invalid_api_credentials')],
        },
      ]);

      // Focus on login field for user to correct credentials
      setTimeout(() => {
        const loginInput =
          document.querySelector(`input[placeholder="xml12345"]`) ||
          document.querySelector(`input[id*="login"]`);

        if (loginInput) {
          loginInput.focus();
          loginInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);

      // fetchInterceptor will handle error notification automatically
      // No need for duplicate notification here
    }
  }

  function handleForceApis() {
    const operatorId = data?._id;
    if (!operatorId) return;

    // Capture credentials before sync — any async re-render or form update during sync
    // could clear them from the form store; we restore them explicitly after
    const preSyncCredentials = form.getFieldsValue(true)?._integrations?.apiCredentials;

    // Immediate feedback: auto-dismisses after 2s — sync continues in background
    appService.notification({
      flag: 'info',
      title: data?.name,
      description: translateX('api_sync_in_progress_background'),
      duration: 2,
    });

    // Keep button disabled during background sync (prevents duplicate triggers)
    setLogsLoading(true);

    // Fire-and-forget — no await, UI remains responsive
    (async () => {
      let attempt = 1;
      const notificationKey = 'api-sync-result';

      try {
        // Retry on timeout; stop immediately on 403 or any other error
        let syncResult;
        while (true) {
          try {
            // axiosService.post returns tuple [docs, result] — docs holds the response body (summary)
            [syncResult] = await axiosService.post({
              url: `/v1/api-integrations/sync/${operatorId}?force=true`,
              returnAsObject: true,
              timeout: 300000, // 5min — coverage scrape: 137 plans × 1500ms delay
            });
            break; // success — exit retry loop
          } catch (err) {
            const isTimeout =
              err.code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout');
            // 403 = backend IP blocked by operator (temporary) — do NOT retry, fail immediately
            const isForbidden = err.response?.status === 403;
            if (!isTimeout || isForbidden) throw err; // non-timeout or blocked → stop retrying
            attempt++;
            // timeout → retry automatically
          }
        }
        const isPartialSync = syncResult?.overallStatus === 'partial';

        // Refresh operator data to get updated exchange rate
        const [updatedOperator] = await axiosService.get({
          url: `/v1/entities/${operatorId}`,
          returnAsObject: true,
        });

        // Update form fields with new values (if sync was successful)
        if (updatedOperator?.parameters?.__exchangeRate) {
          form.setFieldsValue({
            parameters: {
              __exchangeRate: updatedOperator.parameters.__exchangeRate,
              __exchangeRateSuffix: updatedOperator.parameters.__exchangeRateSuffix || 'USD',
            },
          });

          // Update currency state and force SimpleCurrency to re-render
          const newCurrency = updatedOperator.parameters.__exchangeRateSuffix || 'USD';
          setCurrentCurrency(newCurrency);

          // Update last sync date for Alert (use current time as sync just happened)
          setLastSyncDate(new Date().toISOString());

          setExchangeRateKey(prev => prev + 1);
        }

        // Restore credentials after sync — setExchangeRateKey/setFieldsValue above may trigger
        // re-renders that affect the form store; restore explicitly as belt-and-suspenders
        if (preSyncCredentials?.login || preSyncCredentials?.password) {
          form.setFieldValue(
            ['_integrations', 'apiCredentials', 'login'],
            preSyncCredentials.login
          );
          form.setFieldValue(
            ['_integrations', 'apiCredentials', 'password'],
            preSyncCredentials.password
          );
        }

        appService.notification({
          key: notificationKey,
          flag: isPartialSync ? 'warning' : 'success',
          title: data?.name,
          description: isPartialSync
            ? translateX('api_sync_partial_cached_data')
            : translateX('apis_updated_success'),
          duration: isPartialSync ? 6 : 4,
        });
      } catch (error) {
        appService.notification({
          key: notificationKey,
          flag: 'error',
          title: data?.name,
          description: error.message || translateX('error_updating_apis'),
          duration: 6,
        });
      }

      // Refresh logs after sync completes (also resets logsLoading via its own finally)
      await fetchApiLogs();
    })();
  }

  function handleConditionsPdfUpload(file) {
    if (file.type !== 'application/pdf') {
      appService.message({ flag: 'e', text: 'file_invalid_pdf' });
      return Upload.LIST_IGNORE;
    }
    if (file.size > 3 * 1024 * 1024) {
      appService.message({ flag: 'e', text: 'file_too_large_3mb' });
      return Upload.LIST_IGNORE;
    }
    // Run async upload in IIFE — return LIST_IGNORE synchronously so antd
    // never adds the file to its internal list (avoids the blue "!" icon).
    (async () => {
      setConditionsUploading(true);
      try {
        const result = await attachmentsService.upload({ file, generateThumb: false });
        const url = result?.attachment?.storageUrl || result?.storageUrl;
        if (url) {
          form?.setFieldValue(['parameters', 'conditionsFileUrl'], url);
          setConditionsFileUrl(url);
          appService.message({ flag: 's', text: 'file_uploaded_successfully' });
        }
      } catch {
        appService.message({ flag: 'e', text: 'upload_error' });
      } finally {
        setConditionsUploading(false);
      }
    })();
    return Upload.LIST_IGNORE;
  }

  function handleConditionsRemove() {
    if (locked) return false;
    form?.setFieldValue(['parameters', 'conditionsFileUrl'], null);
    setConditionsFileUrl(null);
    return true;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = buildColumns(currentPage, pageSize, token);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={STYLES.container}>
      <Alert type={alertType} showIcon title={translateX(alertMessage)} className="mb-3" />

      <Row gutter={16}>
        {/* CARD 1: API Credentials (NEW) */}
        <Col xs={24} md={8}>
          <Card title={<div className="text-muted">{translateX('api_credentials')}</div>}>
            <SimpleInput
              name={['_integrations', 'apiCredentials', 'login']}
              label={translateX('login')}
              placeholder="xml12345"
              disabled={!hasApiMatch || locked || validatingCredentials || (currentApiEnabled && hasCredentials)}
            />

            <SimplePassword
              name={['_integrations', 'apiCredentials', 'password']}
              label={translateX('password')}
              placeholder="••••••••"
              disabled={!hasApiMatch || locked || validatingCredentials || (currentApiEnabled && hasCredentials)}
              autoComplete="new-password"
            />

            {hasApiMatch && (
              <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                title={translateX('credentials_info')}
                description={translateX('credentials_provided_by_operator')}
                style={STYLES.credentialsAlert}
              />
            )}
          </Card>
        </Col>

        {/* CARD 2: API Integration */}
        <Col xs={24} md={8}>
          <Card title={<div className="text-muted">{translateX('api_integration')}</div>}>
            {hasApiMatch ? (
              <>
                <Descriptions column={1} size="small" bordered className="mb-3">
                  <Descriptions.Item label={translateX('provider')}>
                    <strong>{detectedProvider.name}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="CNPJ">
                    {detectedProvider.cnpjFormatted}
                  </Descriptions.Item>
                </Descriptions>

                <SimpleSwitch
                  name={['_integrations', 'apiEnabled']}
                  label={translateX('enable_auto_sync')}
                  checkedChildren={translateX('enabled')}
                  unCheckedChildren={translateX('disabled')}
                  disabled={locked || validatingCredentials}
                  onChange={checked => handleApiEnabledChange(checked, form)}
                />
              </>
            ) : (
              <Alert
                type="error"
                showIcon
                title={translateX('no_api_for_cnpj')}
                description={
                  cnpjValue
                    ? translateX('cnpj_not_registered', { cnpj: cnpjValue })
                    : translateX('operator_needs_cnpj')
                }
              />
            )}
          </Card>
        </Col>

        {/* CARD 3: Exchange Rate (Existing) */}
        <Col xs={24} md={8}>
          <Card title={<div className="text-muted">{translateX('exchange_rate')}</div>}>
            <SimpleCurrency
              key={`exchange-rate-${exchangeRateKey}`}
              name={['parameters', '__exchangeRate']}
              label={translateX('exchange')}
              min={0.01}
              max={99.99}
              decimalPlaces={2}
              currencies={currencies}
              currencyDefaultValue={currentCurrency}
              onCurrencyChange={currency => {
                form.setFieldValue(['parameters', '__exchangeRateSuffix'], currency);
                setCurrentCurrency(currency);
              }}
              x-currency-style={STYLES.currencyInput}
              disabled={currentApiEnabled || locked}
            />

            {currentApiEnabled &&
              (() => {
                // Get last exchange update date from logs (same source as table)
                const lastExchangeLog = logsData.find(log => log.action === 'exchange_rate');
                const updateDate =
                  lastSyncDate || lastExchangeLog?._metadata?.audit?.createdAt || null;

                const alertText = updateDate
                  ? translateX('exchange_rate_updated_via_api_at', { DATE: parseDateToDisplay({ date: updateDate, showHours: true }) })
                  : translateX('auto_updated_by_api');

                return (
                  <Alert
                    type="info"
                    showIcon
                    icon={<RobotOutlined />}
                    description={alertText}
                    style={STYLES.exchangeRateAlert}
                  />
                );
              })()}
          </Card>
        </Col>
      </Row>

      {/* Conditions PDF */}
      <Card
        className="mt-3"
        title={<div className="text-muted">{translateX('conditions_document')}</div>}
      >
        <Upload
          accept=".pdf"
          listType="picture"
          fileList={conditionsFileUrl ? [{
            uid: '-1',
            name: conditionsFileUrl.split('?')[0].split('/').pop() || 'conditions.pdf',
            status: 'done',
          }] : []}
          maxCount={1}
          beforeUpload={handleConditionsPdfUpload}
          onRemove={handleConditionsRemove}
          onPreview={() => conditionsFileUrl && window.open(conditionsFileUrl, '_blank')}
          iconRender={() => <FilePdfOutlined style={pdfIconStyle} />}
          disabled={locked || conditionsUploading}
        >
          {!conditionsFileUrl && (
            <Button icon={<UploadOutlined />} loading={conditionsUploading} disabled={locked}>
              {translateX('upload')}
            </Button>
          )}
        </Upload>
      </Card>

      {/* API Logs */}
      {true && (
        <Card
          className="mt-3"
          title={<div className="text-muted">{translateX('api_logs')}</div>}
          extra={
            <Button
              type="primary"
              size="small"
              icon={<ReloadOutlined />}
              loading={logsLoading}
              disabled={isSyncDisabled}
              onClick={handleForceApis}
            >
              {translateX('data_sync')}
            </Button>
          }
        >
          <Table
            rowKey={record => record._id?.toString() || `log-${Date.now()}-${Math.random()}`}
            columns={columns}
            dataSource={logsData}
            size="small"
            loading={logsLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total, range) => `${range[0]}-${range[1]} ${translateX('of')} ${total}`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
          />
        </Card>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************

export default OperatorsApiIntegrationTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildCurrencyOptions() {
  return operatorsService.getCurrencies().map(c => ({
    value: c.value,
    label: operatorsService.getCurrencySymbol(c.value),
    text: c.label,
  }));
}

function buildColumns(currentPage, pageSize, token) {
  const lineNumStyle = { fontWeight: 500, color: token.colorTextTertiary };
  const actionIconStyle = { fontSize: 16, color: token.colorPrimary };
  const successIconStyle = { fontSize: 16, color: token.colorSuccess };
  const errorIconStyle = { fontSize: 16, color: token.colorError };

  return [
    {
      title: '#',
      width: 50,
      align: 'center',
      render: (_, __, index) => {
        const lineNumber = (currentPage - 1) * pageSize + index + 1;
        return <span style={lineNumStyle}>{lineNumber}</span>;
      },
    },
    {
      dataIndex: ['_metadata', 'audit', 'createdAt'],
      title: translateX('date'),
      width: 160,
      render: value => (
        <span>{parseDateToDisplay({ date: value, showHours: true, showSeconds: true })}</span>
      ),
    },
    {
      dataIndex: 'action',
      title: translateX('action'),
      width: 180,
      render: value => translateX(`api_action_${value}`),
    },
    {
      dataIndex: 'status',
      title: translateX('status'),
      width: 140,
      render: (_, record) => {
        // Check if it's a warning with no changes (nothing changed)
        const isNothingChanged =
          record?.status === 'warning' &&
          (record?.plansCreated || 0) === 0 &&
          (record?.plansUpdated || 0) === 0 &&
          (record?.plansRemoved || 0) === 0;

        return (
          <Tag
            color={
              record?.status === 'success'
                ? 'green'
                : record?.status === 'failed'
                  ? 'red'
                  : record?.status === 'warning'
                    ? 'gold'
                    : record?.status === 'in_progress'
                      ? 'orange'
                      : 'gray'
            }
            className="text-uppercase"
          >
            {isNothingChanged ? translateX('nothing_changed') : translateX(record?.status)}
          </Tag>
        );
      },
    },
    {
      title: translateX('info'),
      width: 280,
      render: (_, record) => {
        const isManual = record?._metadata?.origin === 1;
        const created = record?.plansCreated || 0;
        const updated = record?.plansUpdated || 0;
        const removed = record?.plansRemoved || 0;
        const isPlansAction = record?.action === 'update_plans';
        const isExchangeAction = record?.action === 'exchange_rate';

        return (
          <Flex justify="start" align="center" gap={12}>
            {/* Manual/Automatic Icon (always show) */}
            {isManual ? (
              <Tooltip title={translateX('manual_sync')}>
                <UserOutlined style={actionIconStyle} />
              </Tooltip>
            ) : (
              <Tooltip title={translateX('automatic_sync')}>
                <RobotOutlined style={successIconStyle} />
              </Tooltip>
            )}

            {/* For update_plans: show quantitative icons */}
            {isPlansAction && (
              <>
                <Tooltip title={translateX('new_plans')}>
                  <Flex align="center" gap={4}>
                    <PlusCircleOutlined style={successIconStyle} />
                    <span style={STYLES.countSpan}>{created}</span>
                  </Flex>
                </Tooltip>

                <Tooltip title={translateX('updated_plans')}>
                  <Flex align="center" gap={4}>
                    <EditOutlined style={actionIconStyle} />
                    <span style={STYLES.countSpan}>{updated}</span>
                  </Flex>
                </Tooltip>

                <Tooltip title={translateX('removed_plans')}>
                  <Flex align="center" gap={4}>
                    <DeleteOutlined style={errorIconStyle} />
                    <span style={STYLES.countSpan}>{removed}</span>
                  </Flex>
                </Tooltip>
              </>
            )}

            {/* For exchange_rate: show formatted description */}
            {isExchangeAction && (
              <span style={STYLES.descriptionSpan}>{formatExchangeDescription(record?.description)}</span>
            )}
          </Flex>
        );
      },
    },
  ];
}

function formatExchangeDescription(description) {
  if (!description) return '';

  // Extract numeric value from exchange rate description (format: "1 USD = R$ 5.2")
  const match = description.match(/R\$\s*([\d.]+)/);

  if (match) {
    const value = parseFloat(match[1]);
    const formatted = value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return description.replace(/R\$\s*[\d.]+/, `R$ ${formatted}`);
  }

  return description;
}
